import React, { useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

import vokuMark from "@/assets/voku-mark.jpeg";
import targetDesign from "@/assets/vision/target-design.jpeg";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ArrowRight, ArrowUpRight, FileText, FlaskConical, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RequestClinicalDataDialog({ children }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    institution: "",
    message: "",
  });

  const canSubmit =
    form.name.trim().length >= 2 &&
    form.email.trim().includes("@") &&
    form.message.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;

    setLoading(true);
    try {
      await axios.post(`${API}/access-requests`, {
        name: form.name,
        email: form.email,
        use_case: "medical",
        message: `${form.institution ? `Institution: ${form.institution}\n\n` : ""}${form.message}`,
        source: "vision_page",
      });

      toast.success("Request received", {
        description: "We’ll share clinical materials with approved evaluators.",
      });
      setOpen(false);
      setForm({ name: "", email: "", institution: "", message: "" });
    } catch (err) {
      toast.error("Couldn’t submit", {
        description: "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[620px] rounded-none border-white/15 bg-black/70 text-white backdrop-blur-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">Request Clinical Data</DialogTitle>
          <DialogDescription className="text-white/70">
            For clinical teams, research labs, and institutional review.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.18em] text-white/60">
                Name
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Your name"
                className="h-11 rounded-none border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.18em] text-white/60">
                Email
              </label>
              <Input
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@hospital.org"
                type="email"
                className="h-11 rounded-none border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.18em] text-white/60">
              Institution (optional)
            </label>
            <Input
              value={form.institution}
              onChange={(e) =>
                setForm((p) => ({ ...p, institution: e.target.value }))
              }
              placeholder="Clinic / Lab / University"
              className="h-11 rounded-none border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.18em] text-white/60">
              What do you need?
            </label>
            <Textarea
              value={form.message}
              onChange={(e) =>
                setForm((p) => ({ ...p, message: e.target.value }))
              }
              placeholder="Study design, target population (AMD/Glaucoma), evaluation timeline, and environment."
              className="min-h-[120px] rounded-none border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-[#00E6A8]/70"
            />
            <div className="text-xs text-white/45">Minimum 10 characters.</div>
          </div>

          <div className="flex flex-col gap-3 pt-2 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2 text-xs text-white/55">
              <Shield className="h-4 w-4 text-white/60" />
              <span>Offline-first intake. No trackers.</span>
            </div>
            <Button
              type="submit"
              disabled={!canSubmit || loading}
              className="h-11 rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black disabled:opacity-60"
            >
              {loading ? "Submitting…" : "Submit"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VisionHeader() {
  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-[#050505]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-[76px] max-w-6xl items-center justify-between px-5 md:px-8">
        <Link to="/" className="group flex items-center gap-3">
          <div className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-none border border-white/15 bg-white/[0.02]">
            <div
              className="absolute -inset-1 -z-10 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background:
                  "radial-gradient(24px 24px at 50% 50%, rgba(0,230,168,0.28), transparent 70%)",
              }}
            />
            <img
              src={vokuMark}
              alt="VOKU mark"
              className="h-full w-full object-cover opacity-95"
            />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-[0.14em] text-white">
              VOKU VISION
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/45">
              Neuro-Optical Prosthesis
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <Link
            to="/"
            className="text-sm text-white/55 transition-colors hover:text-white"
          >
            Home
          </Link>
          <Link
            to="/vision"
            className="text-sm text-white transition-colors"
          >
            Vision
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <RequestClinicalDataDialog>
              <Button className="h-10 rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black">
                Request Clinical Data
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </RequestClinicalDataDialog>
          </div>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 rounded-none border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  Menu
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[320px] rounded-none border-white/10 bg-black/90 text-white backdrop-blur-xl">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-3 text-white">
                    <img
                      src={vokuMark}
                      alt="VOKU mark"
                      className="h-6 w-6 rounded-none object-cover opacity-95"
                    />
                    <div className="leading-tight">
                      <div className="tracking-[0.14em]">VOKU VISION</div>
                      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/45">
                        Neuro-Optical Prosthesis
                      </div>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="mt-6 flex flex-col gap-2">
                  <SheetClose asChild>
                    <Link to="/">
                      <Button
                        variant="ghost"
                        className="h-11 w-full justify-start rounded-none text-white/80 hover:bg-white/10 hover:text-white"
                      >
                        Home
                      </Button>
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/vision">
                      <Button
                        variant="ghost"
                        className="h-11 w-full justify-start rounded-none text-white hover:bg-white/10 hover:text-white"
                      >
                        Vision
                      </Button>
                    </Link>
                  </SheetClose>
                </div>

                <div className="mt-8">
                  <RequestClinicalDataDialog>
                    <Button className="h-11 w-full rounded-none bg-[#00E6A8] text-black hover:bg-white hover:text-black">
                      Request Clinical Data
                      <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </RequestClinicalDataDialog>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}

export default function Vision() {
  const citations = useMemo(
    () => [
      {
        id: "PMC7002244",
        title: "Expansion of Peripheral Visual Field with VR Spectacles.",
        href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7002244/",
      },
      {
        id: "PMC7899512",
        title: "Applications of augmented reality in ophthalmology (Latency constraints).",
        href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7899512/",
      },
      {
        id: "PMC12416520",
        title: "Usefulness of Wearable Electronic Vision Enhancement for AMD.",
        href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12416520/",
      },
      {
        id: "PMID 8861632",
        title: "Display based systems for training eccentric viewing.",
        href: "https://pubmed.ncbi.nlm.nih.gov/8861632/",
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <VisionHeader />

      {/* Background (subtle grid + clinical glow) */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.10)_1px,transparent_1px)] [background-size:72px_72px]" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(780px 520px at 68% 22%, rgba(0,230,168,0.10), transparent 62%), radial-gradient(640px 520px at 20% 70%, rgba(255,255,255,0.04), transparent 60%)",
          }}
        />
      </div>

      {/* HERO */}
      <section className="pt-[118px] md:pt-[132px]">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid gap-12 md:grid-cols-2 md:gap-16">
            <div>
              <Badge className="rounded-none border border-white/15 bg-white/[0.02] px-3 py-2 text-white/70">
                Modular architecture
              </Badge>

              <h1 className="mt-5 text-balance text-4xl font-semibold tracking-tight md:text-5xl">
                Privacy by Default, Intelligence on Demand.
              </h1>
              <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-white/75 md:text-lg">
                The world's first zero-latency neuro-optical prosthesis with an air-gapped analog core and an opt-in decoupled Cognitive Layer.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <RequestClinicalDataDialog>
                  <Button className="h-12 rounded-none bg-[#00E6A8] px-6 text-black hover:bg-white hover:text-black">
                    Request Clinical Data
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                </RequestClinicalDataDialog>
                <a
                  href="#clinical-foundation"
                  className="inline-flex h-12 items-center rounded-none px-2 text-sm text-white/70 transition-colors hover:text-white"
                >
                  Review citations
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>

              <div className="mt-10 grid gap-3 sm:grid-cols-2">
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-5 text-white backdrop-blur-xl">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55">
                    <FlaskConical className="h-4 w-4 text-[#00E6A8]" />
                    Clinical constraint
                  </div>
                  <div className="mt-2 text-lg font-semibold text-white">Latency intolerance</div>
                  <div className="mt-1 text-sm text-white/70">Sensory-motor mismatch drives nausea.</div>
                </Card>
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-5 text-white backdrop-blur-xl">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/55">
                    <FileText className="h-4 w-4 text-[#00E6A8]" />
                    Evidence
                  </div>
                  <div className="mt-2 text-lg font-semibold text-white">Peer-reviewed</div>
                  <div className="mt-1 text-sm text-white/70">Citations below for review.</div>
                </Card>
              </div>
            </div>

            <div className="md:pt-2">
              <div className="relative overflow-hidden rounded-none border border-white/10 bg-white/[0.02] backdrop-blur-xl">
                <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:64px_64px]" />

                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={targetDesign}
                    alt="VOKU Vision target design"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-black/25" />
                </div>

                <div className="relative flex items-center justify-between border-t border-white/10 px-5 py-4">
                  <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/60">
                    target design
                  </div>
                  <div className="text-xs text-white/55">clinical optics • analog-direct</div>
                </div>
              </div>

              <Card className="mt-6 relative overflow-hidden rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
                <div className="absolute inset-0 opacity-[0.10] [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:44px_44px]" />
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                    Summary
                  </div>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-start justify-between gap-6 border border-white/10 bg-black/30 p-4">
                      <div>
                        <div className="text-sm font-medium text-white">Motion-to-Photon</div>
                        <div className="mt-1 text-sm text-white/70">Target end-to-end latency</div>
                      </div>
                      <div className="text-lg font-semibold text-white">&lt; 5ms</div>
                    </div>
                    <div className="flex items-start justify-between gap-6 border border-white/10 bg-black/30 p-4">
                      <div>
                        <div className="text-sm font-medium text-white">Data surface</div>
                        <div className="mt-1 text-sm text-white/70">
                          Base System: 100% Offline | VOKU Cognitive Layer: Opt-in App
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-white">Layered</div>
                    </div>
                    <div className="flex items-start justify-between gap-6 border border-white/10 bg-black/30 p-4">
                      <div>
                        <div className="text-sm font-medium text-white">Targeted application</div>
                        <div className="mt-1 text-sm text-white/70">Low-vision assistance (AMD/Glaucoma)</div>
                      </div>
                      <div className="text-lg font-semibold text-white">Targeted</div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">The Problem</div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                The Clinical Bottleneck: Digital Lag
              </h2>
            </Card>
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <p className="text-sm leading-relaxed text-white/75 md:text-base">
                Clinical trials prove AR helps low-vision patients detect obstacles. However, standard
                digital AR introduces a 12-50ms latency. Peer-reviewed research (PMC7899512) confirms
                this temporal lag causes sensory-motor conflict, resulting in severe nausea and
                disorientation for visually impaired users.
              </p>
              <div className="mt-6 flex items-center justify-between border border-white/10 bg-black/30 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.18em] text-white/55">Digital pipeline</div>
                <div className="text-sm font-semibold text-white">12–50ms</div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex items-end justify-between gap-8">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">Solution</div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Decoupled Analog Architecture
              </h2>
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {[
              {
                title: "<1ms Latency",
                body: "Direct analog signal path prevents motion sickness.",
              },
              {
                title: "Peripheral Awareness",
                body: "Real-time hazard remapping without vestibular disconnect.",
              },
              {
                title: "Central Vision Aid",
                body: "Instantaneous eccentric viewing support.",
              },
              {
                title: "Mission-Critical Reliability",
                body: "Air-gapped, no OS, zero software crashes.",
              },
            ].map((c) => (
              <Card
                key={c.title}
                className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl"
              >
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold text-white">{c.title}</div>
                  <span className="h-2 w-2 rounded-full bg-white/30" />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">{c.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* LAYER 1 + LAYER 2 */}
      <section className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                Architecture
              </div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Privacy by Default, Intelligence on Demand.
              </h2>
            </div>
            <div className="max-w-md text-sm text-white/60">
              A decoupled two-layer design that keeps motion-to-photon latency stable.
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">Layer 1</div>
              <h3 className="mt-2 text-xl font-semibold text-white">
                The Air-Gapped Analog Core
                <span className="block text-sm font-semibold text-white/60">
                  (Privacy by Default)
                </span>
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                The basic hardware works out-of-the-box. No Wi‑Fi, no cloud accounts, and no subscriptions
                required to see—ultimate data privacy (HIPAA-friendly).
              </p>
              <div className="mt-6 space-y-3">
                <div className="rounded-none border border-white/10 bg-black/30 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                    Visual feed
                  </div>
                  <div className="mt-2 text-sm text-white/80">
                    Analog Raster Compression (Glaucoma/AMD) → direct route to micro-display
                    <span className="text-white/60"> (Motion-to-Photon &lt;5ms)</span>
                  </div>
                </div>
                <div className="rounded-none border border-white/10 bg-black/30 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                    Vestibular sync
                  </div>
                  <div className="mt-2 text-sm text-white/80">
                    If the internet goes down, the patient’s vision remains stable and balance remains
                    aligned—no buffering, no vestibular mismatch.
                  </div>
                </div>
              </div>
            </Card>

            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">Layer 2</div>
              <h3 className="mt-2 text-xl font-semibold text-white">
                VOKU Cognitive Layer (V.C.L.)
                <span className="block text-sm font-semibold text-white/60">
                  (Intelligence on Demand)
                </span>
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-white/70 md:text-base">
                A premium, opt-in clinical add-on running on the patient’s smartphone. The visual signal is
                physically split (Y‑Cable): the primary path stays air‑gapped for vision, while a cloned feed
                goes to the phone for background spatial analysis.
              </p>
              <div className="mt-6 space-y-3">
                <div className="rounded-none border border-white/10 bg-black/30 p-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                    Asynchronous OSD injection
                  </div>
                  <div className="mt-2 text-sm text-white/80">
                    The Cognitive Layer returns low-bandwidth coordinate data over Bluetooth. Headset hardware
                    injects On-Screen Display (OSD) warnings directly over the analog video—without slowing,
                    buffering, or transforming the primary visual feed.
                  </div>
                </div>
              </div>
              <div className="mt-6 rounded-none border border-white/10 bg-white/[0.02] p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-white/55">Transport</div>
                <div className="mt-1 text-sm font-semibold text-white">Bluetooth (coords only)</div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CLINICAL USE CASES */}
      <section className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                VOKU Cognitive Layer
              </div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Clinical Use Cases for the Cognitive Layer
              </h2>
            </div>
            <div className="max-w-md text-sm text-white/60">
              Optional, opt-in clinical augmentation designed to reduce falls, errors, and cognitive load.
            </div>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                Fall Prevention
              </div>
              <div className="mt-2 text-lg font-semibold text-white">
                Hazard Detection
              </div>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                The V.C.L. continuously scans peripheral blind spots and injects flashing OSD warning frames
                around tripping hazards (stairs, curbs), drastically reducing the risk of falls for elderly
                glaucoma patients.
              </p>
            </Card>

            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                Medication Safety
              </div>
              <div className="mt-2 text-lg font-semibold text-white">Medication Adherence</div>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                Real-time spatial text recognition reads prescription bottles and pill labels, providing audio
                feedback or high-contrast OSD text to prevent dosing errors.
              </p>
            </Card>

            <Card className="rounded-none border-white/10 bg-white/[0.03] p-6 text-white backdrop-blur-xl">
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                Cognitive Load
              </div>
              <div className="mt-2 text-lg font-semibold text-white">Cognitive Offloading</div>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                Context awareness helps patients navigate complex environments, reducing the mental fatigue
                associated with severe visual impairment.
              </p>
            </Card>
          </div>
        </div>
      </section>


      {/* EVIDENCE */}
      <section id="clinical-foundation" className="pt-20 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                Scientific Evidence
              </div>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Clinical Foundation
              </h2>
            </div>
            <div className="max-w-md text-sm text-white/60">
              Links are formatted as academic citations for quick verification.
            </div>
          </div>

          <div className="mt-8 grid gap-4">
            {citations.map((c) => (
              <a
                key={c.id}
                href={c.href}
                target="_blank"
                rel="noreferrer"
                className="group"
              >
                <Card className="rounded-none border-white/10 bg-white/[0.03] p-5 text-white backdrop-blur-xl transition-colors duration-200 group-hover:bg-white/[0.045]">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-white/55">
                        {c.id}
                      </div>
                      <div className="mt-1 text-sm font-medium text-white/85">
                        {c.title}
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-2 text-sm text-white/60 transition-colors group-hover:text-white">
                      Open
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                </Card>
              </a>
            ))}
          </div>

          <div className="mt-10 border-t border-white/10 pt-8 text-sm text-white/55">
            <span className="text-white/75">Disclaimer:</span> VOKU Vision is currently in the R&amp;D and prototyping phase.
            It is an investigational device and has not been cleared or approved by the FDA. The information
            on this page is for informational purposes only and does not constitute medical advice. VOKU is not
            intended to diagnose, treat, cure, or prevent any disease. Optional AI features described on this
            page are conceptual and may vary by configuration, availability, and clinical validation.
          </div>
        </div>
      </section>

      <footer className="mt-20 border-t border-white/10 pb-10 pt-10 md:mt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-white/70">VOKU Technologies Inc. | Est. 2026, Florida, USA.</div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <Badge className="w-fit rounded-none border border-white/15 bg-white/[0.02] text-white/70">
                US Patent Pending 63/970,453
              </Badge>
              <a
                href="mailto:contact@vokutech.com"
                className="text-sm text-white/65 transition-colors hover:text-white"
              >
                contact@vokutech.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
